import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {User} from '../models/user.model';
import {Router} from '@angular/router';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {AuthResponseData} from '../models/auth-response-data.model';
import {ServerStatusMessageResponseModel} from '../models/server-status-message-response.model';
import {SystemNotificationModel} from '../models/system-notification.model';
import {environment} from "../../../environments/environment";
import {CookieService} from "ngx-cookie-service";
import {safeParseJson} from "../utils/safe-json";
import {clearCatalogSourceFilter} from "../utils/catalog-source-filter.storage";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  userSubject = new BehaviorSubject<User>(null);

  /**
   * The in-flight server verification, shared so that app bootstrap and a simultaneous route
   * activation coalesce onto one `/auth/verify` request instead of racing. Released when the
   * request settles, so each subsequent guarded navigation re-validates against the server.
   */
  private sessionValidation$: Observable<boolean> | null = null;

  /**
   * Must match the server's JWT lifetime (`expiresIn: '10h'` in api.iolr's auth controller).
   * The two literals still live in separate repositories; a spec locks this one so they cannot
   * drift silently.
   */
  static readonly SESSION_DURATION_HOURS = 10;

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService
  ) {
  }

  private static handleError(errorRes: HttpErrorResponse) {
    const fallback = 'Error logging in. Please try again.';
    const raw = errorRes?.error?.message ?? errorRes?.error ?? fallback;
    const message = typeof raw === 'string' ? raw : fallback;
    return throwError(() => ({ message }));
  }

  onLogin(data) {
    const params = {
      email: data.email,
      password: data.password
    };
    return this.http.post<AuthResponseData>(`${environment.apiURL}/auth/login`, JSON.stringify(params)).pipe(
      catchError(AuthService.handleError),
      tap(resData => {
        this.setUserData(resData);
      })
    );
  }

  onEditProfile(data) {
    const params = {
      mobile: data.mobile,
      phoneCountryCode: data.phoneCountryCode
    };
    return this.http.put<AuthResponseData>(`${environment.apiURL}/auth`, JSON.stringify(params)).pipe(
      catchError(AuthService.handleError),
      tap(resData => {
        this.setUserData(resData);
      })
    );
  }

  onLoginWithGetParams(userHash: string, token: string): Observable<any> {
    return this.http.get(`${environment.apiURL}/auth?hash=${userHash}&token=${token}`).pipe(
      catchError(AuthService.handleError),
      tap(resData => {
        this.setUserData(resData);
      })
    );
  }

  onForgotPassword(params) {
    return this.http.post<ServerStatusMessageResponseModel>(`${environment.apiURL}/forgot-password`, JSON.stringify(params)).pipe(
      catchError(AuthService.handleError)
    );
  }

  onResetPassword(data) {
    return this.http.post<AuthResponseData>(`${environment.apiURL}/reset-password`, JSON.stringify(data)).pipe(
      catchError(AuthService.handleError),
      tap(resData => {
        this.setUserData(resData);
      })
    );
  }

  /**
   * auto login user except on activate account route
   *
   * Owns its subscription deliberately: `ensureSessionValidated()` is cold, so bootstrap has
   * to subscribe or no verification would ever be issued. Unlike the previous implementation
   * this no longer navigates on failure — bootstrap runs on public routes too, and a stale
   * cookie must not eject a visitor from /graph.
   */
  autoLogin(): void {
    const href = window.location.href;
    if (href.indexOf('login?u=') !== -1 || href.indexOf('/register/') !== -1) {
      return;
    }

    this.ensureSessionValidated().subscribe();
  }

  /**
   * The single session-validation path, shared by app bootstrap and the route guard (#34).
   *
   * Ordered and short-circuiting: steps 1-3 only ever *deny*, and do so without an HTTP call;
   * only the server can grant. Local expiry inspection is a cheap fast-reject, never proof
   * that a token is valid — that remains the server's decision.
   */
  ensureSessionValidated(): Observable<boolean> {
    if (this.sessionValidation$) {
      return this.sessionValidation$;
    }

    const userData = safeParseJson<User>(this.cookieService.get('iolrUserData'));

    // 1 & 2 — no cookie, malformed cookie, or no token in it.
    if (!userData || !userData.token) {
      this.expireSession();
      return of(false);
    }

    // 3 — locally expired.
    if (AuthService.isLocallyExpired(userData)) {
      this.expireSession();
      return of(false);
    }

    // 4 — the authority.
    this.sessionValidation$ = this.http.post<AuthResponseData>(
      `${environment.apiURL}/auth/verify`,
      {token: userData.token}
    ).pipe(
      map(() => {
        this.onTransmitUserData(userData);
        return true;
      }),
      catchError(() => {
        this.expireSession();
        return of(false);
      }),
      finalize(() => {
        this.sessionValidation$ = null;
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );

    return this.sessionValidation$;
  }

  private static isLocallyExpired(userData: User): boolean {
    const expiry = (userData as { expirationDate?: string | Date }).expirationDate;
    if (!expiry) {
      return false;
    }

    const expiresAt = new Date(expiry).getTime();
    return Number.isFinite(expiresAt) && expiresAt <= Date.now();
  }

  /**
   * The one idempotent stale-session cleanup primitive (#34). Deliberately does **not**
   * navigate: where the user should go depends on whether they are on a guarded route, and
   * routing that decision through here is what previously threw people off the public graph
   * page. Callers navigate; this only clears state.
   */
  expireSession(): void {
    // Emitting only on a real transition keeps repeat calls — two parallel 401s, or a guard
    // rejection followed by an interceptor rejection — from re-notifying subscribers.
    if (this.userSubject.value !== null) {
      this.onTransmitUserData(null);
    }
    this.cookieService.delete('iolrUserData', '/');
    // Drop the session-scoped catalogue source filter so the next login starts at "All".
    clearCatalogSourceFilter();
  }

  deleteUserLocalStorageData(): void {
    this.expireSession();
  }

  onLogout() {
    this.expireSession();
    void this.router.navigateByUrl('/');
  }

  onRegister(data) {
    return this.http.post<SystemNotificationModel>(`${environment.apiURL}/register`, JSON.stringify(data));
  }

  public setUserData(userData) {
    userData.expirationDate = new Date(
      new Date().getTime() + (AuthService.SESSION_DURATION_HOURS * 60 * 60 * 1000)
    );
    // Must be an explicit Date. ngx-cookie-service reads a *number* here as a count of days,
    // so passing the hour count left the cookie — and the navbar's logged-in state — alive for
    // ~10 days against a 10-hour JWT. That gap is the window #34 reproduces in.
    this.cookieService.set('iolrUserData', JSON.stringify(userData), userData.expirationDate, '/');
    this.onTransmitUserData(userData);
  }

  private onTransmitUserData(userData: User | null): void {
    this.userSubject.next(userData);
  }

}
